import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Menu, Search, X } from "lucide-react";
import { getMapCartographieData, getNearbyProduits, type MapEtablissementItem, type NearbyProduitGroup, type NearbyProduitItem } from "../../api/etablissement";
import { MOCK_MAP_CARTOGRAPHIE_DATA } from "./medicalMapsMockData";

/** `true` : appelle l’API établissement (session requise). Par défaut : mocks pour la carte publique. */
const USE_LIVE_MAP_API = import.meta.env.VITE_MAP_CARTOGRAPHIE_LIVE === "true";

const COTONOU = { lat: 6.37, lng: 2.42 };

const L: any = (globalThis as any).L;

type Facility = {
  id: string;
  name: string;
  address1: string;
  address2: string;
  distanceM: number;
  tags: ("LITS" | "BANQUE DE SANG" | "PRODUITS")[];
  lat: number;
  lng: number;
  bloodSummary?: Record<string, number>;
  bedsAvailable?: number;
  productsCount?: number;
  tauxOccupation?: number;
};

type ProductItem = NearbyProduitItem;
type ProductGroup = NearbyProduitGroup;

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] as const;
type FilterValue = "ALL" | "LITS" | "PRODUITS" | (typeof BLOOD_GROUPS)[number];

function mapEtablissementsToFacilities(etablissements: MapEtablissementItem[] = []): Facility[] {
  return etablissements
    .filter((e) => e.coordonnees != null || e.lat != null || e.latitude != null)
    .map((e) => {
      const lat = e.coordonnees?.latitude ?? e.lat ?? e.latitude ?? 0;
      const lng = e.coordonnees?.longitude ?? e.lng ?? e.longitude ?? 0;

      const bedsCount = e.litsCount ?? e.bedsAvailable ?? 0;
      const prodCount = e.produitsCount ?? e.productsCount ?? 0;

      // Construire bloodSummary depuis sangParGroupe (poches disponibles uniquement)
      const bloodSummary: Record<string, number> = {};
      if (e.sangParGroupe) {
        for (const [group, stats] of Object.entries(e.sangParGroupe)) {
          const avail = (stats as { disponible?: number }).disponible ?? 0;
          if (avail > 0) bloodSummary[group] = avail;
        }
      } else if (e.bloodSummary) {
        Object.assign(bloodSummary, e.bloodSummary);
      }
      const hasSang = Object.keys(bloodSummary).length > 0;

      const tags: ("LITS" | "BANQUE DE SANG" | "PRODUITS")[] = [];
      if (bedsCount > 0) tags.push("LITS");
      if (hasSang) tags.push("BANQUE DE SANG");
      if (prodCount > 0) tags.push("PRODUITS");
      if (tags.length === 0) tags.push("PRODUITS");

      return {
        id: e.id,
        name: (e.nom ?? e.name ?? "").toString(),
        address1: (e.city ?? e.commune ?? "").toString(),
        address2: (e.commune ?? "").toString(),
        distanceM: 0,
        tags,
        lat,
        lng,
        bloodSummary,
        bedsAvailable: bedsCount,
        productsCount: prodCount,
        tauxOccupation: e.tauxOccupationLits,
      };
    });
}

export default function MedicalMaps() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterValue>("ALL");
  const [radiusKm, setRadiusKm] = useState(5);
  const [userPos, setUserPos] = useState<{ lat: number; lng: number } | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const markersByIdRef = useRef<Record<string, any>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Recherche produit/médicament ──────────────────────────────────────────
  const [medicamentQuery, setMedicamentQuery] = useState("");
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [isProductMode, setIsProductMode] = useState(false);

  const loadMapData = useCallback(() => {
    setLoading(true);
    setError(null);

    const applyPayload = (data: { etablissements?: MapEtablissementItem[] }) => {
      setFacilities(mapEtablissementsToFacilities(data?.etablissements));
    };

    if (!USE_LIVE_MAP_API) {
      applyPayload(MOCK_MAP_CARTOGRAPHIE_DATA);
      setLoading(false);
      return;
    }

    const center = userPos ?? COTONOU;
    getMapCartographieData({ latitude: center.lat, longitude: center.lng, radius: radiusKm })
      .then((res) => {
        applyPayload(res.ok ? (res.data ?? { etablissements: [] }) : MOCK_MAP_CARTOGRAPHIE_DATA);
      })
      .catch(() => applyPayload(MOCK_MAP_CARTOGRAPHIE_DATA))
      .finally(() => setLoading(false));
  }, [userPos, radiusKm]);

  useEffect(() => {
    loadMapData();
  }, [loadMapData]);

  useEffect(() => {
    const q = medicamentQuery.trim();
    if (q.length < 2) {
      setIsProductMode(false);
      setProductGroups([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setProductSearchLoading(true);
      setIsProductMode(true);
      const center = userPos ?? COTONOU;
      const res = await getNearbyProduits({
        latitude: center.lat,
        longitude: center.lng,
        radius: radiusKm,
        q,
      });
      setProductSearchLoading(false);
      if (!res.ok) { setProductGroups([]); return; }
      setProductGroups(res.data);
    }, 400);
    return () => window.clearTimeout(t);
  }, [medicamentQuery, userPos, radiusKm]);

  const inRadius = (f: Facility) => {
    if (!userPos) return true;
    const R = 6371;
    const dLat = ((f.lat - userPos.lat) * Math.PI) / 180;
    const dLng = ((f.lng - userPos.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((userPos.lat * Math.PI) / 180) * Math.cos((f.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = R * c;
    return dist <= radiusKm;
  };

  const distanceKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const A =
      Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    const C = 2 * Math.atan2(Math.sqrt(A), Math.sqrt(1 - A));
    return R * C;
  };

  const list = useMemo(() => {
    if (isProductMode) {
      const matched: Facility[] = productGroups
        .filter((g) => g.distance !== null && g.etablissement.coordonnees !== null)
        .map((g) => ({
          id: g.etablissement.id,
          name: g.etablissement.nom,
          address1: g.etablissement.adresse ?? g.etablissement.type ?? "",
          address2: "",
          distanceM: 0,
          tags: ["PRODUITS" as const],
          lat: g.etablissement.coordonnees!.latitude,
          lng: g.etablissement.coordonnees!.longitude,
        }));
      if (!userPos) return matched;
      return matched
        .map((f) => ({ ...f, _dkm: distanceKm(userPos, f) }) as Facility & { _dkm: number })
        .sort((a, b) => a._dkm - b._dkm);
    }
    const matchesFilter = (f: Facility): boolean => {
      if (filter === "ALL") return true;
      if (filter === "LITS" || filter === "PRODUITS") return f.tags.includes(filter);
      if (BLOOD_GROUPS.includes(filter as (typeof BLOOD_GROUPS)[number])) {
        return (f.bloodSummary?.[filter] ?? 0) > 0;
      }
      return true;
    };
    const filtered = facilities.filter(
      (f) =>
        matchesFilter(f) &&
        (f.name.toLowerCase().includes(query.toLowerCase()) || f.address2.toLowerCase().includes(query.toLowerCase())) &&
        inRadius(f)
    );
    if (userPos) {
      return filtered
        .map((f) => ({ ...f, _dkm: distanceKm(userPos, f) }) as Facility & { _dkm: number })
        .sort((a, b) => a._dkm - b._dkm);
    }
    return filtered;
  }, [query, filter, userPos, radiusKm, facilities, isProductMode, productGroups]);

  const prodGroupsAvecGPS = useMemo(
    () => productGroups.filter((g) => g.distance !== null && g.etablissement.coordonnees !== null),
    [productGroups]
  );

  const prodGroupsSansGPS = useMemo(
    () => productGroups.filter((g) => g.distance === null),
    [productGroups]
  );

  const chip = (label: string) => (
    <span className="px-3 h-7 inline-flex items-center rounded-full text-xs border" style={{ borderColor: "#D1E2DD", background: "#EDEFF0" }}>
      {label}
    </span>
  );

  const formatDistance = (km: number) => {
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
  };

  useEffect(() => {
    if (mapRef.current) return;
    let cancelled = false;

    const mount = () => {
      if (cancelled) return;
      const el = document.getElementById("map");
      if (!el || !L) {
        setTimeout(mount, 100);
        return;
      }
      // Le conteneur est déjà initialisé par Leaflet (remontage HMR / StrictMode)
      if ((el as any)._leaflet_id) return;
      const map = L.map("map", { zoomControl: true }).setView([6.37, 2.42], 12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
      }).addTo(map);
      L.control.zoom({ position: "bottomright" }).addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 0);
    };
    mount();

    const onResize = () => mapRef.current?.invalidateSize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
    markersByIdRef.current = {};
    list.forEach((f) => {
      let fillColor = "#00553E";
      if (isProductMode) {
        const group = productGroups.find((g) => g.etablissement.id === f.id);
        if (group) {
          const tousDisponibles = group.produits.every((p) => p.statut === "disponible");
          fillColor = tousDisponibles ? "#00553E" : "#F97316";
        }
      }
      const marker = L.circleMarker([f.lat, f.lng], {
        radius: 8,
        color: fillColor,
        weight: 2,
        fillColor,
        fillOpacity: 0.85,
      }).addTo(map);
      marker.bindPopup(`<b>Position de ${f.name}</b><br/>${f.address1}<br/>${f.address2}`);
      marker.bindTooltip(`${f.name}`, { direction: "top", opacity: 0.9 });
      marker.on("click", () => {
        setSelectedId(f.id);
        map.setView([f.lat, f.lng], Math.max(map.getZoom(), 14));
      });
      markersRef.current.push(marker);
      markersByIdRef.current[f.id] = marker;
    });
    if (list.length) {
      const group = L.featureGroup(markersRef.current);
      map.fitBounds(group.getBounds().pad(0.2));
    }
  }, [list, isProductMode, productGroups]);

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setUserPos(p);
      const map = mapRef.current;
      if (map) {
        const userMarker = L.circleMarker([p.lat, p.lng], {
          radius: 6,
          color: "#1E40AF",
          weight: 2,
          fillColor: "#1E40AF",
          fillOpacity: 0.9,
        }).addTo(map);
        userMarker.bindPopup("Vous êtes ici");
        userMarker.bindTooltip("Votre position", { direction: "top", opacity: 0.9 });
        map.setView([p.lat, p.lng], 13);
      }
    });
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    setTimeout(() => map.invalidateSize(), 0);
  }, [selectedId, query, filter]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#F3F3F2] flex items-center justify-center">
        <div className="text-gray-600">Chargement de la carte…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-[#F3F3F2] flex flex-col items-center justify-center p-4">
        <p className="text-gray-700 text-center">{error}</p>
        <button
          type="button"
          onClick={() => loadMapData()}
          className="mt-4 h-10 px-4 rounded-[10px] font-medium"
          style={{ backgroundColor: "#00553E", color: "#fff" }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F3F3F2]">
      {/* Full-screen map */}
      <div className="absolute inset-0 z-0">
        <div id="map" className="absolute inset-0 w-full h-full" />
      </div>

      {/* Header overlay on the map */}
      <header
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-2.5 bg-white/95 backdrop-blur-sm border-b"
        style={{ borderColor: "#D1E2DD" }}
      >
        <button onClick={() => navigate(-1)} className="text-[#00553E]" aria-label="Retour">
          <ArrowLeft size={20} />
        </button>
        <div className="relative flex-1 mx-3">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#00553E]/50" />
          <input
            placeholder="Médicament, produit…"
            value={medicamentQuery}
            onChange={(e) => setMedicamentQuery(e.target.value)}
            className="w-full h-9 rounded-[8px] border pl-8 pr-8 text-sm bg-[#EDEFF0] text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30"
            style={{ borderColor: "#D1E2DD" }}
          />
          {productSearchLoading ? (
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#00553E] border-t-transparent" />
            </div>
          ) : medicamentQuery ? (
            <button
              type="button"
              aria-label="Effacer"
              onClick={() => setMedicamentQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
        <img src="/agbaza-logo.png" alt="agbaza" className="h-10 w-auto" />
      </header>

      {/* Floating control panel over the map */}
      <div
        className="absolute left-3 top-14 z-10 w-[calc(100vw-1.5rem)] max-w-md max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col rounded-[10px] border shadow-lg bg-white/95 backdrop-blur-sm"
        style={{ borderColor: "#00553E" }}
      >
        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="relative">
            <input
              placeholder="Rechercher"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-12 rounded-[10px] border px-4 bg-[#EDEFF0]"
              style={{ borderColor: "#D1E2DD" }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button className="h-9 w-9 rounded-[10px] border flex items-center justify-center shrink-0" style={{ borderColor: "#D1E2DD" }}>
              <Menu size={16} />
            </button>
            <div className="flex flex-wrap gap-2">
              {(["LITS", "PRODUITS"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter(filter === t ? "ALL" : t)}
                  className={`h-9 px-2 sm:px-3 rounded-[10px] border text-xs sm:text-sm whitespace-nowrap ${filter === t ? "text-white" : ""}`}
                  style={{ borderColor: "#00553E", background: filter === t ? "#00553E" : "transparent" }}
                >
                  {t}
                </button>
              ))}
              {BLOOD_GROUPS.map((bg) => (
                <button
                  key={bg}
                  onClick={() => setFilter(filter === bg ? "ALL" : bg)}
                  className={`h-9 px-2 sm:px-3 rounded-[10px] border text-xs sm:text-sm whitespace-nowrap ${filter === bg ? "text-white" : ""}`}
                  style={{ borderColor: "#00553E", background: filter === bg ? "#00553E" : "transparent" }}
                >
                  {bg}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={locate}
              className="h-9 px-3 rounded-[10px] border bg-white inline-flex items-center gap-2 whitespace-nowrap text-sm shrink-0"
              style={{ borderColor: "#D1E2DD", color: "#00553E" }}
            >
              <MapPin size={16} /> Ma position
            </button>
            <div className="flex items-center gap-3 sm:flex-1 min-w-[140px]">
              <span className="text-sm text-gray-700 shrink-0">Rayon</span>
              <input className="w-28 sm:flex-1 min-w-0" type="range" min={1} max={20} value={radiusKm} onChange={(e) => setRadiusKm(parseInt(e.target.value, 10))} />
              <span className="text-sm text-gray-700 w-10 text-right shrink-0 whitespace-nowrap">{radiusKm} km</span>
            </div>
          </div>

          {isProductMode ? (
            /* ── Mode recherche produit ── */
            <div className="divide-y -mx-4 px-4" style={{ borderColor: "#E5E7EB" }}>
              {!productSearchLoading && productGroups.length === 0 && medicamentQuery.trim() && (
                <div className="py-8 text-center text-gray-500">Aucun résultat pour « {medicamentQuery} »</div>
              )}
              {!productSearchLoading && prodGroupsAvecGPS.length === 0 && prodGroupsSansGPS.length > 0 && (
                <div className="py-4 text-sm text-gray-500">
                  Aucun établissement localisé sur la carte, mais {prodGroupsSansGPS.length} établissement{prodGroupsSansGPS.length > 1 ? "s" : ""} trouvé{prodGroupsSansGPS.length > 1 ? "s" : ""} sans coordonnées GPS :
                </div>
              )}
              {prodGroupsAvecGPS.map((group) => {
                const etab = group.etablissement;
                const matchedFacility = list.find((f) => f.id === etab.id) as (Facility & { _dkm?: number }) | undefined;
                return (
                  <div
                    key={etab.id}
                    className="py-4 first:pt-0 cursor-pointer"
                    onClick={() => {
                      if (!etab.coordonnees) return;
                      const map = mapRef.current;
                      if (map) map.setView([etab.coordonnees.latitude, etab.coordonnees.longitude], 15);
                      const marker = markersByIdRef.current[etab.id];
                      if (marker) marker.openPopup();
                      setSelectedId(etab.id);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold min-w-0 truncate" style={{ color: "#00553E" }}>
                        {etab.nom}
                      </div>
                      <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        {group.produits.length} produit{group.produits.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    {etab.adresse && (
                      <div className="mt-0.5 text-sm text-gray-600 truncate">{etab.adresse}</div>
                    )}
                    {userPos && matchedFacility?._dkm != null && (
                      <div className="text-xs text-gray-500">{formatDistance(matchedFacility._dkm)}</div>
                    )}
                    <div className="mt-2 space-y-1">
                      {group.produits.slice(0, 3).map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2 text-xs text-[#0B1F16]">
                          <span className="truncate">{p.nom}</span>
                          <div className="shrink-0 flex items-center gap-1">
                            <span className={`px-1.5 py-0.5 rounded-full text-xs ${p.statut === "disponible" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                              {p.statut === "disponible" ? "Disponible" : "Rupture de stock"}
                            </span>
                            {p.prix?.prixVente != null && (
                              <span className="text-gray-500">{p.prix.prixVente} F</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {group.produits.length > 3 && (
                        <div className="text-xs text-gray-400">+{group.produits.length - 3} autre(s)</div>
                      )}
                    </div>
                    {etab.coordonnees && (
                      <div className="mt-3">
                        <a
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border text-[#00553E] bg-white"
                          style={{ borderColor: "#D1E2DD" }}
                          target="_blank"
                          rel="noreferrer"
                          href={`https://www.google.com/maps/dir/?api=1&origin=${
                            userPos ? `${userPos.lat},${userPos.lng}` : "Current+Location"
                          }&destination=${etab.coordonnees.latitude},${etab.coordonnees.longitude}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Itinéraire <ArrowRight size={16} />
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
              {prodGroupsSansGPS.length > 0 && (
                <>
                  {prodGroupsAvecGPS.length > 0 && (
                    <div className="pt-4 mt-2 text-xs text-gray-400">
                      {prodGroupsSansGPS.length} établissement{prodGroupsSansGPS.length > 1 ? "s" : ""} sans localisation GPS :
                    </div>
                  )}
                  {prodGroupsSansGPS.map((group) => (
                    <div key={group.etablissementId ?? group.etablissement.id} className="py-3">
                      <div className="font-semibold text-sm truncate" style={{ color: "#00553E" }}>{group.etablissement.nom}</div>
                      {group.etablissement.adresse && (
                        <div className="mt-0.5 text-xs text-gray-500 truncate">{group.etablissement.adresse}</div>
                      )}
                      <div className="mt-1.5 space-y-1">
                        {group.produits.slice(0, 2).map((p) => (
                          <div key={p.id} className="flex items-center justify-between gap-2 text-xs text-[#0B1F16]">
                            <span className="truncate">{p.nom}</span>
                            <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-xs ${p.statut === "disponible" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                              {p.statut === "disponible" ? "Disponible" : "Rupture"}
                            </span>
                          </div>
                        ))}
                        {group.produits.length > 2 && (
                          <div className="text-xs text-gray-400">+{group.produits.length - 2} autre(s)</div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : (
            /* ── Mode établissements (défaut) ── */
            <div className="divide-y -mx-4 px-4" style={{ borderColor: "#E5E7EB" }}>
              {list.map((f) => (
                <div
                  key={f.id}
                  className="py-4 cursor-pointer first:pt-0"
                  onClick={() => {
                    const map = mapRef.current;
                    if (map) map.setView([f.lat, f.lng], 15);
                    const marker = markersByIdRef.current[f.id];
                    if (marker) marker.openPopup();
                    setSelectedId(f.id);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="font-semibold min-w-0" style={{ color: "#00553E" }}>
                      {f.name}
                    </div>
                    <div className="text-sm text-gray-600 shrink-0 ml-2">
                      {userPos ? formatDistance((f as Facility & { _dkm?: number })._dkm ?? 0) : `${f.distanceM} m`}
                    </div>
                  </div>
                  <div className="mt-1 text-sm text-gray-700 truncate">{f.address1}</div>
                  <div className="text-sm text-gray-700 truncate">{f.address2}</div>
                  {(() => {
                    const bedsOk = typeof f.bedsAvailable === "number" && f.bedsAvailable > 0;
                    const prodOk = typeof f.productsCount === "number" && f.productsCount > 0;
                    const bloodOk = !!f.bloodSummary && Object.values(f.bloodSummary).some((v) => Number(v) > 0);
                    const badge = (ok: boolean, label: string) => (
                      <span className={`px-2 py-1 rounded-full text-xs ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {label}: {ok ? "Disponible" : "Indisponible"}
                      </span>
                    );
                    return (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {badge(bedsOk, "Lits")}
                        {badge(prodOk, "Produits")}
                        {badge(bloodOk, "Sang")}
                      </div>
                    );
                  })()}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {f.tags.includes("LITS") && chip("LITS")}
                    {f.tags.includes("BANQUE DE SANG") && chip("BANQUE DE SANG")}
                    {f.tags.includes("PRODUITS") && chip("PRODUITS")}
                  </div>
                  <div className="mt-3">
                    <a
                      className="inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border text-[#00553E] bg-white"
                      style={{ borderColor: "#D1E2DD" }}
                      target="_blank"
                      rel="noreferrer"
                      href={`https://www.google.com/maps/dir/?api=1&origin=${
                        userPos ? `${userPos.lat},${userPos.lng}` : "Current+Location"
                      }&destination=${f.lat},${f.lng}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Itinéraire <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              ))}
              {query.trim() && list.length === 0 && (
                <div className="py-8 text-center text-gray-500">Aucun établissement trouvé</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel (right side, when facility selected) */}
      {selectedId &&
          (() => {
            const source = facilities.find((r) => r.id === selectedId);
            const f = list.find((x) => x.id === selectedId) || source;
            const dkm = userPos && f ? distanceKm(userPos, f) : null;
            return (
              <div
                className="fixed right-3 md:right-6 top-14 w-[92vw] sm:w-[380px] max-w-[95vw] bg-white/95 backdrop-blur-sm rounded-[10px] border shadow-lg p-4 space-y-3 z-30"
                style={{ borderColor: "#E5E7EB" }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-lg" style={{ color: "#00553E" }}>
                      {f?.name}
                    </div>
                    <div className="text-sm text-gray-700">{f?.address1}</div>
                    <div className="text-sm text-gray-700">{f?.address2}</div>
                  </div>
                  <button className="text-gray-500" onClick={() => setSelectedId(null)} aria-label="Fermer">
                    <X size={18} />
                  </button>
                </div>
                {dkm !== null && <div className="text-sm text-gray-600">Distance: {formatDistance(dkm)}</div>}
                <div className="flex gap-2 flex-wrap">
                  {f?.tags.includes("LITS") && chip("LITS")}
                  {f?.tags.includes("BANQUE DE SANG") && chip("BANQUE DE SANG")}
                  {f?.tags.includes("PRODUITS") && chip("PRODUITS")}
                </div>
                {isProductMode && selectedId && (() => {
                  const prods = productGroups.find((g) => g.etablissement.id === selectedId)?.produits ?? [];
                  if (prods.length === 0) return null;
                  return (
                    <div>
                      <div className="mb-1 text-sm font-medium text-[#0B1F16]">
                        Produits trouvés ({prods.length})
                      </div>
                      <div className="space-y-1">
                        {prods.map((p) => (
                          <div key={p.id} className="flex items-center justify-between gap-2 border-b py-1 text-xs" style={{ borderColor: "#F3F4F6" }}>
                            <span className="truncate text-[#0B1F16]">{p.nom}</span>
                            <div className="shrink-0 flex items-center gap-1 whitespace-nowrap">
                              <span className={`px-1.5 py-0.5 rounded-full text-xs ${p.statut === "disponible" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                                {p.statut === "disponible" ? "Disponible" : "Rupture de stock"}
                              </span>
                              {p.prix?.prixVente != null && (
                                <span className="text-gray-500">{p.prix.prixVente} F</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {source && (
                  <div className="space-y-2 text-sm">
                    {(() => {
                      const bedsOk = typeof source.bedsAvailable === "number" && source.bedsAvailable > 0;
                      const prodOk = typeof source.productsCount === "number" && source.productsCount > 0;
                      const bloodOk = !!source.bloodSummary && Object.values(source.bloodSummary).some((v) => Number(v) > 0);
                      const badge = (ok: boolean, label: string) => (
                        <span className={`px-2 py-1 rounded-full text-xs ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {label}: {ok ? "Disponible" : "Indisponible"}
                        </span>
                      );
                      return (
                        <div className="flex flex-wrap gap-2 mb-1">
                          {badge(bedsOk, "Lits")}
                          {badge(prodOk, "Produits")}
                          {badge(bloodOk, "Sang")}
                        </div>
                      );
                    })()}
                    {typeof source.bedsAvailable === "number" && source.bedsAvailable > 0 && (
                      <div className="flex justify-between">
                        <span>Lits disponibles</span>
                        <span>{source.bedsAvailable}</span>
                      </div>
                    )}
                    {source.tauxOccupation != null && (
                      <div className="flex justify-between">
                        <span>Taux d'occupation</span>
                        <span>{source.tauxOccupation.toFixed(1)} %</span>
                      </div>
                    )}
                    {source.productsCount != null && source.productsCount > 0 && (
                      <div className="flex justify-between">
                        <span>Produits</span>
                        <span>{source.productsCount}</span>
                      </div>
                    )}
                    {source.bloodSummary && Object.keys(source.bloodSummary).length > 0 && (
                      <div>
                        <div className="font-medium mb-1">Banque de sang</div>
                        <div className="grid grid-cols-3 gap-1">
                          {Object.entries(source.bloodSummary).map(([k, v]) => (
                            <div key={k} className="flex justify-between bg-gray-100 rounded px-2 py-1">
                              <span>{k}</span>
                              <span>{v as any}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {f && (
                  <div className="pt-2 flex justify-end">
                    <a
                      className="inline-flex items-center gap-2 h-9 px-3 rounded-[10px] border text-[#00553E] bg-white"
                      style={{ borderColor: "#D1E2DD" }}
                      target="_blank"
                      rel="noreferrer"
                      href={`https://www.google.com/maps/dir/?api=1&origin=${
                        userPos ? `${userPos.lat},${userPos.lng}` : "Current+Location"
                      }&destination=${f.lat},${f.lng}`}
                    >
                      Itinéraire <ArrowRight size={16} />
                    </a>
                  </div>
                )}
              </div>
            );
          })()}
    </div>
  );
}

